import Stepper from '@/components/Stepper';
import ToolBar from '@/components/ToolBar';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { FormProvider, useForm } from 'react-hook-form';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { displayError } from '@/lib/error';
import { HIEditClientDetails } from './HIEditClientDetails';
import { HIEditServiceDetails } from './HIEditServiceDetails';
import {
  HIEditStep1Schema,
  HIEditStep2Schema,
  type HIEditFormValues,
  type HIWorkOrderUpdatePayload,
} from './hi.schema';
import {
  cancelHIWorkOrderEdit,
  assignHomeInspectionWorkOrder,
  useAssignHIServer,
  useFetchHIWorkOrderForEdit,
  useUpdateHIWorkOrder,
} from '@/services/homeInspectionService';
import { toast } from '@/components/ui/use-toast';
import { useUserStore } from '@/stores/user';
import AlertBox from '@/components/AlertBox';
import Loader from '@/components/Loader';
import { canAccessHomeInspectionWorkOrders } from './hiPermissions';
import axios from 'axios';
import { hasFeature } from '@/services/featureService';
import { Feature } from '@/utils/constant';

const STEPS = [
  { title: 'Client Information', isCompleted: false },
  { title: 'Service Details', isCompleted: false },
];

const STEP_SCHEMAS = [HIEditStep1Schema, HIEditStep2Schema];

export const HIEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const loggedInUser = useUserStore((state) => state.user);

  const [activeStep, setActiveStep] = useState(0);
  const [steps, setSteps] = useState(STEPS);
  const [currentSchema, setCurrentSchema] = useState<(typeof STEP_SCHEMAS)[number]>(STEP_SCHEMAS[0]);
  const [workOrderNumber, setWorkOrderNumber] = useState('');
  const [isLoaded, setIsLoaded] = useState(false);
  const [isConcurrentBlocked, setIsConcurrentBlocked] = useState(false);
  const [hasFetchError, setHasFetchError] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);

  const cancelledRef = useRef(false);
  const loadedAssignmentRef = useRef<{ teamId: string | null; userId: string | null } | null>(null);

  const form = useForm<HIEditFormValues>({
    resolver: zodResolver(currentSchema),
    mode: 'all',
    defaultValues: {
      rushType: 'NONE',
      sameDayRushDisplay: false,
      nextDayRushDisplay: false,
      prePaid: false,
    },
  });

  useEffect(() => {
    setCurrentSchema(STEP_SCHEMAS[activeStep]);
    setTimeout(async () => {
      await form.trigger();
      form.clearErrors();
    }, 50);
  }, [activeStep]);

  const queryClient = useQueryClient();
  const { mutate: fetchForEdit, status: fetchStatus } = useFetchHIWorkOrderForEdit();
  const { mutate: updateWorkOrder, status: updateStatus } = useUpdateHIWorkOrder();
  const { mutate: assignServer, status: assignStatus } = useAssignHIServer();

  useEffect(() => {
    if (!id) return;
    fetchForEdit(id, {
      onSuccess: (data) => {
        if (data.isEditing && data.editingById !== loggedInUser?.userId) {
          setIsConcurrentBlocked(true);
          return;
        }
        setWorkOrderNumber(data.workOrderNumber);
        form.reset({
          clientId: data.clientId,
          clientName: data.clientName,
          contactId: data.contactId ?? '',
          contactEmail: data.contactEmail,
          fileNumber: data.fileNumber,
          caseNumber: data.caseNumber,
          caseName: data.caseName,
          workOrderNumber: data.workOrderNumber,
          lawyer: data.lawyer,
          represents: data.represents ?? undefined,
          rushType: data.rushType,
          sameDayRushDisplay: data.rushType === 'SAME_DAY',
          nextDayRushDisplay: data.rushType === 'NEXT_DAY',
          prePaid: false,
          mortgageCompanyOrPlaintiff: data.mortgageCompanyOrPlaintiff,
          ownerOrDefendantName: data.ownerOrDefendantName,
          addressLine1: data.propertyAddress,
          addressLine2: '',
          city: data.city,
          state: data.state,
          zipCode: data.zipCode,
          lastServeDate: data.lastServeDate ? new Date(data.lastServeDate) : null,
          serverId: data.serverId ?? undefined,
          descriptionOfDocuments: data.descriptionOfDocuments,
          teamId: data.teamId ?? undefined,
          userId: data.userId ?? undefined,
        });
        loadedAssignmentRef.current = {
          teamId: data.teamId ?? null,
          userId: data.userId ?? null,
        };
        setIsLoaded(true);
      },
      onError: (error) => {
        if (axios.isAxiosError(error) && error.response?.status === 409) {
          setIsConcurrentBlocked(true);
          return;
        }
        setHasFetchError(true);
        displayError(error, 5000);
      },
    });
  }, [id]);

  const triggerCancel = useCallback(() => {
    if (!id || cancelledRef.current) return;
    cancelledRef.current = true;
    cancelHIWorkOrderEdit(id).catch(() => undefined);
  }, [id]);

  useEffect(() => {
    return () => {
      triggerCancel();
    };
  }, [triggerCancel]);

  useEffect(() => {
    const handleBeforeUnload = () => triggerCancel();
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [triggerCancel]);

  if (!canAccessHomeInspectionWorkOrders(loggedInUser)) {
    return <Navigate to="/not-found" />;
  }

  if (hasFetchError) {
    return <Navigate to="/home-inspection" />;
  }

  const scrollToTop = () => {
    const el = document.getElementById('toolbar');
    if (el) el.scrollIntoView({ block: 'end', behavior: 'smooth' });
  };

  const nextStep = async () => {
    const isValid = await form.trigger();
    if (isValid) {
      setSteps((prev) => prev.map((s, i) => (i === activeStep ? { ...s, isCompleted: true } : s)));
      setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
      setTimeout(scrollToTop, 100);
    }
  };

  const prevStep = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
    setTimeout(scrollToTop, 100);
  };

  const saveHandler = async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    const values = form.getValues();
    const propertyAddress = [values.addressLine1, values.addressLine2].filter(Boolean).join(' ');

    const payload: HIWorkOrderUpdatePayload = {
      contactId: values.contactId,
      lawyer: values.lawyer ?? null,
      represents: values.represents ?? null,
      rushType: values.rushType,
      mortgageCompanyOrPlaintiff: values.mortgageCompanyOrPlaintiff.trim(),
      propertyAddress,
      city: values.city.trim(),
      state: values.state,
      zipCode: values.zipCode.trim(),
      ownerOrDefendantName: values.ownerOrDefendantName.trim(),
      descriptionOfDocuments: values.descriptionOfDocuments ?? null,
    };

    updateWorkOrder(
      { id: id!, payload },
      {
        onSuccess: async () => {
          cancelledRef.current = true; // suppress cancel-edit only after confirmed success

          setIsAssigning(true);
          try {
            const loaded = loadedAssignmentRef.current;
            const nextTeamId = values.teamId ?? null;
            const nextUserId = values.userId ?? null;
            const canAssign =
              hasFeature(Feature.HOME_INSPECTION_WORK_ORDER_CREATE);
            const teamChanged = canAssign && loaded !== null && nextTeamId !== loaded.teamId;
            const userChanged = canAssign && loaded !== null && nextUserId !== loaded.userId;
            let assignmentPersistedTeamId = loaded?.teamId ?? null;
            let assignmentPersistedUserId = loaded?.userId ?? null;
            let assignmentClearWarning: string | null = null;

            // Team assign clears userId on the API — only call when team actually changed.
            // If team changed and a user is still selected, re-apply user after team assign.
            if (teamChanged && nextTeamId) {
              await assignHomeInspectionWorkOrder(id!, { teamId: nextTeamId });
              assignmentPersistedTeamId = nextTeamId;
              assignmentPersistedUserId = null;
              if (nextUserId) {
                await assignHomeInspectionWorkOrder(id!, { userId: nextUserId });
                assignmentPersistedUserId = nextUserId;
              }
            } else if (userChanged && nextUserId) {
              await assignHomeInspectionWorkOrder(id!, { userId: nextUserId });
              assignmentPersistedUserId = nextUserId;
            } else if (!teamChanged && userChanged && !nextUserId && loaded?.teamId) {
              // Clear user only: re-assign same team (API nulls userId, keeps team)
              await assignHomeInspectionWorkOrder(id!, { teamId: loaded.teamId });
              assignmentPersistedTeamId = loaded.teamId;
              assignmentPersistedUserId = null;
            } else if (teamChanged && !nextTeamId) {
              // Clearing team is not supported by assign-team-user — keep server assignment
              assignmentClearWarning =
                'Work order updated, but clearing team assignment is not supported. Previous team/user were kept.';
            }

            if (values.serverId) {
              await new Promise<void>((resolve, reject) => {
                assignServer(
                  { id: id!, serverId: values.serverId! },
                  {
                    onSuccess: () => resolve(),
                    onError: (error) => reject(error),
                  }
                );
              });
            }
            loadedAssignmentRef.current = {
              teamId: assignmentPersistedTeamId,
              userId: assignmentPersistedUserId,
            };
            void queryClient.invalidateQueries({ queryKey: ['hi-work-order-detail', id] });
            toast({
              variant: assignmentClearWarning ? 'destructive' : 'default',
              description:
                assignmentClearWarning ?? 'Home Inspection work order updated successfully.',
              duration: assignmentClearWarning ? 7000 : 5000,
            });
            navigate('/home-inspection');
          } catch (error) {
            displayError(error, 5000);
            navigate('/home-inspection');
          } finally {
            setIsAssigning(false);
          }
        },
        onError: (error) => {
          displayError(error, 5000);
        },
      }
    );
  };

  if (isConcurrentBlocked) {
    return (
      <AlertBox
        title="THIS WORK ORDER IS CURRENTLY BEING EDITED BY ANOTHER USER"
        description="Another user is currently editing this work order. Please try again later."
        confirmBtnLabel="Go Back"
        confirmBtnClick={() => navigate('/home-inspection')}
        displayCancelButton={false}
        open={true}
      />
    );
  }

  return (
    <div className="w-full scrollbar-hide">
      <div className="w-full">
        <ToolBar title={workOrderNumber ? `Edit: W/O ${workOrderNumber}` : 'Edit Work Order'} />
        <Loader isLoading={fetchStatus === 'pending' || (!isLoaded && !hasFetchError)}>
          <div className="mt-3 flex justify-center pl-24">
            <Stepper steps={steps} activeStep={activeStep} goToStep={Function.prototype as (step: number) => void} />
          </div>

          <FormProvider {...form}>
            {activeStep === 0 && <HIEditClientDetails />}
            {activeStep === 1 && <HIEditServiceDetails />}
          </FormProvider>

          <div className="flex justify-end mt-4 mb-8 w-full gap-2">
            {activeStep > 0 && (
              <Button
                variant="outline"
                className="text-primary py-2 px-4 rounded-md border-primary flex justify-center w-[125px] h-[48px] hover:bg-[#EDF5FF]"
                onClick={prevStep}
              >
                Back
              </Button>
            )}
            {activeStep < steps.length - 1 && (
              <Button
                className="text-[#fff] py-2 px-4 rounded-md bg-primary w-[125px] h-[48px] border-primary flex justify-center hover:bg-[#12375C] hover:text-white disabled:bg-primary"
                onClick={nextStep}
                disabled={!form.formState.isValid}
              >
                Next
              </Button>
            )}
            {activeStep === steps.length - 1 && (
              <Button
                className="text-[#fff] py-2 px-4 rounded-md bg-primary w-[125px] h-[48px] border-primary flex justify-center hover:bg-[#12375C] hover:text-white"
                onClick={saveHandler}
                disabled={!form.formState.isValid || updateStatus === 'pending' || assignStatus === 'pending' || isAssigning}
              >
                Save
              </Button>
            )}
          </div>
        </Loader>
      </div>
    </div>
  );
};
